import type { ScopeView } from "@cosmosai/api-client";
import { CheckIcon, ChevronDownIcon, LayersIcon } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { useProjects, useWorkspaces } from "../../api/registry-queries";
import { useGo } from "../../app/route-link";
import { Button } from "../../ui/button";
import { cn } from "../../ui/cn";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { routes } from "./paths";
import { useCosmosContext } from "./use-context";

const label = (scope: ScopeView) => scope.displayName ?? scope.name;

function Choice({
  current,
  onSelect,
  children,
}: {
  current: boolean;
  onSelect: () => void;
  children: ReactNode;
}) {
  return (
    <li>
      <button
        type="button"
        aria-current={current || undefined}
        onClick={onSelect}
        className={cn(
          "flex w-full items-center gap-2 rounded-[6px] px-2.5 py-1.5 text-left text-base text-ink hover:bg-hover",
          current && "font-semibold",
        )}
      >
        <span className="min-w-0 flex-1 truncate">{children}</span>
        {current && <CheckIcon aria-hidden className="size-4 text-primary" />}
      </button>
    </li>
  );
}

/** Projects grouped by the team that owns them, teams in name order. */
function byTeam(projects: ScopeView[]): Array<[string, ScopeView[]]> {
  const teams = new Map<string, ScopeView[]>();
  for (const project of projects) {
    const team = project.team ?? "";
    teams.set(team, [...(teams.get(team) ?? []), project]);
  }
  return [...teams.entries()].sort(([a], [b]) => a.localeCompare(b));
}

function ProjectChoices({
  workspace,
  project,
  onPick,
}: {
  workspace: string;
  project: string | null;
  onPick: (project: string | null) => void;
}) {
  const { t } = useTranslation();
  const projects = useProjects(workspace);
  if (projects.isPending) return <p className="px-2.5 py-1.5 text-ink-3">{t("context.loading")}</p>;
  if (projects.isError) return <p className="px-2.5 py-1.5 text-crit-ink">{t("context.failed")}</p>;
  return (
    <>
      <ul>
        <Choice current={project === null} onSelect={() => onPick(null)}>
          {t("context.allProjects")}
        </Choice>
      </ul>
      {byTeam(projects.data).map(([team, members]) => (
        <section key={team} aria-label={t("context.team", { team })}>
          <h3 className="px-2.5 pt-2 pb-0.5 text-xs font-semibold text-ink-3">
            {t("context.team", { team })}
          </h3>
          <ul>
            {members.map((member) => (
              <Choice
                key={member.name}
                current={member.name === project}
                onSelect={() => onPick(member.name)}
              >
                {label(member)}
              </Choice>
            ))}
          </ul>
        </section>
      ))}
    </>
  );
}

/** The top bar's context: workspace, then a project grouped by team; navigation keeps `env`. */
export function ContextSelector() {
  const { t } = useTranslation();
  const { workspace, project } = useCosmosContext();
  const workspaces = useWorkspaces();
  const go = useGo();
  const [open, setOpen] = useState(false);
  const current = workspaces.data?.find((w) => w.name === workspace);
  const pick = (action: () => void) => {
    setOpen(false);
    action();
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className="min-w-0 gap-1.5 px-2.5 font-normal"
          aria-label={t("context.change", {
            context: [workspace, project].filter(Boolean).join(" › ") || t("context.none"),
          })}
        >
          <LayersIcon aria-hidden className="size-[18px]" />
          <span className="truncate text-ink-2 max-sm:hidden">
            {current ? label(current) : (workspace ?? t("context.choose"))}
          </span>
          {project && (
            <>
              <span aria-hidden className="text-ink-3 max-sm:hidden">
                ›
              </span>
              <b className="truncate font-semibold text-ink">{project}</b>
            </>
          )}
          <ChevronDownIcon aria-hidden className="size-[15px]" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="max-h-[70vh] w-80 overflow-y-auto">
        <h2 className="px-2.5 pt-1 pb-0.5 text-xs font-semibold text-ink-3">
          {t("context.workspace")}
        </h2>
        {workspaces.isError && <p className="px-2.5 py-1.5 text-crit-ink">{t("context.failed")}</p>}
        <ul>
          {workspaces.data?.map((w) => (
            <Choice
              key={w.name}
              current={w.name === workspace}
              onSelect={() => pick(() => go(routes.home(w.name)))}
            >
              {label(w)} <span className="font-mono text-sm text-ink-3">{w.name}</span>
            </Choice>
          ))}
        </ul>
        {workspace && (
          <>
            <hr className="my-1.5 border-line" />
            <h2 className="px-2.5 pt-1 pb-0.5 text-xs font-semibold text-ink-3">
              {t("context.project")}
            </h2>
            <ProjectChoices
              workspace={workspace}
              project={project}
              onPick={(name) =>
                pick(() => go(routes.specs(workspace, name ? { project: name } : {})))
              }
            />
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
